import React, { useEffect, useState } from 'react';
import Layout from './components/Layout';
import Home from './components/Home';
import CheckIn from './components/CheckIn';
import CoachingSuggestion from './components/CoachingSuggestion';
import ThemeOverview from './components/ThemeOverview';
import ThemeDetail from './components/ThemeDetail';
import ExercisePlayer from './components/ExercisePlayer';
import PersonalDiary from './components/PersonalDiary';
import SmartFeed from './components/SmartFeed';
import HelpMode from './components/HelpMode';
import Settings from './components/Settings';
import QuestionFlow from './components/QuestionFlow';
import QuestionnaireFlow from './components/QuestionnaireFlow';
import MicroFeedback from './components/MicroFeedback';
import Soundscape from './components/Soundscape';
import TherapistChat from './components/TherapistChat';
import Favorites from './components/Favorites';
import Onboarding from './components/Onboarding';
import CheckInSummary from './components/CheckInSummary';
import { UserDataProvider } from './context/UserDataContext';
export function App() {
  const [currentView, setCurrentView] = useState('onboarding');
  const [userMood, setUserMood] = useState(null);
  const [checkInCompleted, setCheckInCompleted] = useState(false);
  const [checkInData, setCheckInData] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [currentTheme, setCurrentTheme] = useState('');
  const [currentExercise, setCurrentExercise] = useState('');
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  // Check if user has completed onboarding
  useEffect(() => {
    // In a real app, this would check local storage or a backend API
    const hasCompletedOnboarding = localStorage.getItem('onboardingCompleted');
    if (hasCompletedOnboarding === 'true') {
      setOnboardingCompleted(true);
      setCurrentView('home');
    }
  }, []);
  const sampleQuestions = [{
    id: 'q1',
    text: 'Hoe zou je je algemene stemming van vandaag omschrijven?',
    type: 'text'
  }, {
    id: 'q2',
    text: 'Hoe goed heb je geslapen de afgelopen nacht?',
    type: 'scale'
  }, {
    id: 'q3',
    text: 'Wat zou je vandaag willen bereiken?',
    type: 'choice',
    choices: ['Meer focus', 'Minder stress', 'Beter slapen', 'Meer energie', 'Anders']
  }];
  const handleQuickCheckIn = mood => {
    setUserMood(mood);
    setCheckInData({
      mood,
      type: 'quick'
    });
    setCurrentView('checkin-summary');
    setShowFeedback(true);
  };
  const handleFullCheckInComplete = data => {
    setUserMood(data.mood);
    setCheckInData(data);
    setCheckInCompleted(true);
    // Store the complete profile data for later use
    // This could be expanded to update a user profile in a real app
    console.log('Psychological profile data:', data);
    setCurrentView('checkin-summary');
    setShowFeedback(true);
  };
  const handleQuestionFlowComplete = () => {
    setCurrentView('coaching');
    setShowFeedback(true);
  };
  const handleQuestionnaireComplete = answers => {
    // In a real app, you would process the answers here
    console.log('Questionnaire answers:', answers);
    setCurrentView('coaching');
    setShowFeedback(true);
  };
  const handleThemeSelect = themeId => {
    setCurrentTheme(themeId);
    setCurrentView('theme-detail');
  };
  const handleStartExercise = exerciseId => {
    setCurrentExercise(exerciseId);
    setCurrentView('exercise-player');
  };
  const handleExerciseComplete = () => {
    // Go back to the previous view
    if (currentTheme) {
      setCurrentView('theme-detail');
    } else {
      setCurrentView('coaching');
    }
  };
  const handleBackToHome = () => {
    setCurrentView('home');
  };
  const handleFavoriteItemSelect = (type: string, id: string) => {
    if (type === 'theme') {
      setCurrentTheme(id);
      setCurrentView('theme-detail');
    } else if (type === 'exercise') {
      setCurrentExercise(id);
      setCurrentView('exercise-player');
    } else if (type === 'soundscape') {
      setCurrentView('soundscapes');
      // In a real app, you would select the specific soundscape
    }
  };
  const handleOnboardingComplete = userData => {
    console.log('Onboarding completed with user data:', userData);
    setUserProfile(userData);
    setOnboardingCompleted(true);
    localStorage.setItem('onboardingCompleted', 'true');
    setCurrentView('home');
  };
  const handleOnboardingSkip = () => {
    setOnboardingCompleted(true);
    localStorage.setItem('onboardingCompleted', 'true');
    setCurrentView('home');
  };
  const renderContent = () => {
    switch (currentView) {
      case 'onboarding':
        return <Onboarding onComplete={handleOnboardingComplete} onSkip={handleOnboardingSkip} data-id="element-0" />;
      case 'home':
        return <Home onStartMoment={() => setCurrentView('checkin')} onQuickCheckIn={handleQuickCheckIn} onStartQuestionnaire={() => setCurrentView('questionnaire')} onExploreThemes={() => setCurrentView('themes')} onOpenSoundscapes={() => setCurrentView('soundscapes')} onOpenDiary={() => setCurrentView('diary')} onStartExercise={handleStartExercise} setCurrentView={setCurrentView} data-id="element-1" />;
      case 'checkin':
        return <CheckIn onComplete={handleFullCheckInComplete} initialMood={userMood} data-id="element-2" />;
      case 'checkin-summary':
        return <CheckInSummary checkInData={checkInData} onViewSuggestions={() => setCurrentView('coaching')} onBackToHome={handleBackToHome} onStartExercise={handleStartExercise} data-id="element-3" />;
      case 'questions':
        return <QuestionFlow questions={sampleQuestions} onComplete={handleQuestionFlowComplete} onSkip={() => setCurrentView('coaching')} data-id="element-4" />;
      case 'questionnaire':
        return <QuestionnaireFlow onComplete={handleQuestionnaireComplete} onBack={handleBackToHome} data-id="element-5" />;
      case 'coaching':
        return <CoachingSuggestion mood={userMood} onExploreThemes={() => setCurrentView('themes')} onBackToHome={handleBackToHome} onStartExercise={handleStartExercise} data-id="element-6" />;
      case 'themes':
        return <ThemeOverview onThemeSelect={handleThemeSelect} onBackToHome={handleBackToHome} data-id="element-7" />;
      case 'theme-detail':
        return <ThemeDetail themeId={currentTheme} onBack={() => setCurrentView('themes')} onStartExercise={handleStartExercise} data-id="element-8" />;
      case 'exercise-player':
        return <ExercisePlayer exerciseId={currentExercise} onBack={() => currentTheme ? setCurrentView('theme-detail') : setCurrentView('coaching')} onComplete={handleExerciseComplete} data-id="element-9" />;
      case 'soundscapes':
        return <Soundscape onBack={handleBackToHome} data-id="element-10" />;
      case 'diary':
        return <PersonalDiary onBack={handleBackToHome} data-id="element-11" />;
      case 'feed':
        return <SmartFeed onBack={handleBackToHome} onStartExercise={handleStartExercise} data-id="element-12" />;
      case 'help':
        return <HelpMode onClose={() => setCurrentView('home')} data-id="element-13" />;
      case 'settings':
        return <Settings onBack={handleBackToHome} data-id="element-14" />;
      case 'therapist-chat':
        return <TherapistChat onBack={handleBackToHome} data-id="element-15" />;
      case 'favorites':
        return <Favorites onBack={handleBackToHome} onItemSelect={handleFavoriteItemSelect} data-id="element-16" />;
      default:
        return <Home onStartMoment={() => setCurrentView('checkin')} onQuickCheckIn={handleQuickCheckIn} onStartQuestionnaire={() => setCurrentView('questionnaire')} onExploreThemes={() => setCurrentView('themes')} onOpenSoundscapes={() => setCurrentView('soundscapes')} onOpenDiary={() => setCurrentView('diary')} onStartExercise={handleStartExercise} setCurrentView={setCurrentView} data-id="element-17" />;
    }
  };
  return <UserDataProvider data-id="element-18">
      <Layout currentView={currentView} setCurrentView={setCurrentView} data-id="element-19">
        {renderContent()}
        {showFeedback && (currentView === 'coaching' || currentView === 'checkin-summary') && <MicroFeedback message="Bedankt voor je check-in. We hebben je suggesties aangepast." onClose={() => setShowFeedback(false)} data-id="element-20" />}
      </Layout>
    </UserDataProvider>;
}